import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AudioTrack } from '@livekit/components-react'
import { Dialog, Notice } from '@/components/play'
import { useEventLiveKitAudioTrackRefs } from '@/components/livekit/EventLiveKitProvider'
import { WorkspaceVideo } from './WorkspaceVideo'
import type { StreamResource } from '@/types/events'
import './workspace-camera.css'

export { CameraPicture, CameraStatus, useCameraState } from './WorkspaceMedia'

export function WorkspaceCamera({
  stream,
  index,
  selected,
  featured,
  compact,
  onPreview,
  onDetails,
  onVisibilityChange,
  onFeature,
  featureBusy,
  canFeature,
  featureDisabledReason,
}: {
  stream: StreamResource
  index: number
  selected: boolean
  featured: boolean
  compact?: boolean
  onPreview: () => void
  onDetails: () => void
  onVisibilityChange?: (visible: boolean) => Promise<void>
  onFeature?: () => void | Promise<void>
  featureBusy?: boolean
  canFeature?: boolean
  featureDisabledReason?: string
}) {
  return (
    <article
      className={`pw-camera ${selected ? 'pw-camera--selected' : ''} ${compact ? 'pw-camera--compact' : ''}`}
      data-camera-id={stream.id}
    >
      <WorkspaceVideo
        stream={stream}
        showName
        number={index + 1}
        selected={selected}
        featured={featured}
        onPreview={onPreview}
        onDetails={onDetails}
        onVisibilityChange={onVisibilityChange}
        onFeature={onFeature}
        featureBusy={featureBusy}
        canFeature={canFeature}
        featureDisabledReason={featureDisabledReason}
      />
    </article>
  )
}

type FullscreenStage = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}
type FullscreenVideo = HTMLVideoElement & {
  webkitSupportsFullscreen?: boolean
  webkitDisplayingFullscreen?: boolean
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
  webkitPresentationMode?: string
  webkitSetPresentationMode?: (mode: 'fullscreen' | 'inline') => void
}
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

function currentFullscreenElement() {
  return document.fullscreenElement || (document as FullscreenDocument).webkitFullscreenElement
}

async function leavePreviewFullscreen(stage: FullscreenStage | null, video: FullscreenVideo | null) {
  if (stage && currentFullscreenElement() === stage) {
    const exit = document.exitFullscreen || (document as FullscreenDocument).webkitExitFullscreen
    if (exit) await exit.call(document)
  }
  if (video) {
    if (video.webkitExitFullscreen) video.webkitExitFullscreen()
    else video.webkitSetPresentationMode?.('inline')
  }
}

function usePreviewFullscreen(streamId?: string) {
  const stageRef = useRef<FullscreenStage | null>(null)
  const ownedStage = useRef<FullscreenStage | null>(null)
  const nativeVideo = useRef<FullscreenVideo | null>(null)
  const nativeActive = useRef(false)
  const generation = useRef(0)
  const pending = useRef(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function syncFullscreen() {
    const video = nativeVideo.current
    setFullscreen(Boolean(
      (ownedStage.current && currentFullscreenElement() === ownedStage.current) ||
      nativeActive.current || video?.webkitDisplayingFullscreen ||
      video?.webkitPresentationMode === 'fullscreen',
    ))
  }

  useEffect(() => {
    setFullscreen(false)
    setBusy(false)
    setError('')
    const nativeChanged = (event: Event) => {
      if (event.target !== nativeVideo.current) return
      if (event.type === 'webkitbeginfullscreen') nativeActive.current = true
      if (event.type === 'webkitendfullscreen') nativeActive.current = false
      if (event.type === 'webkitpresentationmodechanged') {
        nativeActive.current = nativeVideo.current?.webkitPresentationMode === 'fullscreen'
      }
      syncFullscreen()
    }
    document.addEventListener('fullscreenchange', syncFullscreen)
    document.addEventListener('webkitfullscreenchange', syncFullscreen)
    const nativeEvents = ['webkitbeginfullscreen', 'webkitendfullscreen', 'webkitpresentationmodechanged']
    nativeEvents.forEach((name) => document.addEventListener(name, nativeChanged, true))
    return () => {
      generation.current++
      pending.current = false
      document.removeEventListener('fullscreenchange', syncFullscreen)
      document.removeEventListener('webkitfullscreenchange', syncFullscreen)
      nativeEvents.forEach((name) => document.removeEventListener(name, nativeChanged, true))
      void leavePreviewFullscreen(ownedStage.current, nativeVideo.current).catch(() => undefined)
      ownedStage.current = null
      nativeVideo.current = null
      nativeActive.current = false
    }
  }, [streamId])

  async function toggleFullscreen() {
    if (pending.current) return
    const stage = stageRef.current
    if (!stage) return
    const version = generation.current
    pending.current = true
    setBusy(true)
    setError('')
    try {
      if (fullscreen || currentFullscreenElement() === stage) {
        await leavePreviewFullscreen(ownedStage.current, nativeVideo.current)
        if (version === generation.current) syncFullscreen()
        return
      }
      ownedStage.current = stage
      const request = (stage as { requestFullscreen?: () => Promise<void> }).requestFullscreen || stage.webkitRequestFullscreen
      if (request) {
        try {
          await request.call(stage)
          if (version !== generation.current) {
            await leavePreviewFullscreen(stage, null)
            return
          }
          syncFullscreen()
          return
        } catch {
          if (version !== generation.current) return
          // iPhone Safari can reject element fullscreen but support native video.
        }
      }

      const video = stage.querySelector('video') as FullscreenVideo | null
      if (video && video.webkitSupportsFullscreen !== false) {
        nativeVideo.current = video
        if (video.webkitEnterFullscreen) {
          try {
            video.webkitEnterFullscreen()
            syncFullscreen()
            return
          } catch {
            // Try WebKit's presentation-mode API when the older API rejects.
          }
        }
        if (video.webkitSetPresentationMode) {
          video.webkitSetPresentationMode('fullscreen')
          syncFullscreen()
          return
        }
      }
      setError(request
        ? 'Fullscreen couldn’t open. Try again.'
        : 'Fullscreen is not supported here. Keep watching in this preview.')
    } catch {
      if (version === generation.current) {
        setError(fullscreen
          ? 'Fullscreen couldn’t close. Use Escape or your device’s Done control.'
          : 'Fullscreen couldn’t open. Try again.')
      }
    } finally {
      if (version === generation.current) {
        pending.current = false
        setBusy(false)
      }
    }
  }

  return { stageRef, fullscreen, busy, error, toggleFullscreen }
}

export function WorkspacePreview({
  stream,
  onClose,
  featured,
  onDetails,
  onVisibilityChange,
  onFeature,
  featureBusy,
  canFeature,
  featureDisabledReason,
  onSetup,
  info,
}: {
  stream: StreamResource | null
  onClose: () => void
  featured?: boolean
  onDetails?: () => void
  onVisibilityChange?: (visible: boolean) => Promise<void>
  onFeature?: () => void | Promise<void>
  featureBusy?: boolean
  canFeature?: boolean
  featureDisabledReason?: string
  onSetup?: () => void
  info?: ReactNode
}) {
  const [audioStreamId, setAudioStreamId] = useState<string | null>(null)
  const audio = !!stream && audioStreamId === stream.id
  const { stageRef, fullscreen, busy, error, toggleFullscreen } = usePreviewFullscreen(stream?.id)
  const audioRefs = useEventLiveKitAudioTrackRefs()
  useEffect(() => setAudioStreamId(null), [stream?.id])
  const track = audioRefs.find(
    (ref) =>
      !ref.participant.isLocal &&
      ref.participant.identity === stream?.attributes.participant_identity &&
      ref.publication.isSubscribed,
  )
  return (
    <Dialog
      open={!!stream}
      onOpenChange={(open, details) => {
        if (open) return
        // Escape leaves fullscreen first; the next Escape closes the preview.
        if (details.reason === 'escape-key' && fullscreen) {
          details.cancel()
          void toggleFullscreen()
          return
        }
        onClose()
      }}
      size="media"
      hideTitle
      title={stream?.attributes.name || 'Camera preview'}
      description="Live camera preview. Audio is off until you enable it."
      className="pw-preview-dialog"
    >
      <div ref={stageRef} className="pw-preview-stage" data-fullscreen-root data-fullscreen={fullscreen || undefined}>
        {stream && (
          <WorkspaceVideo
            key={stream.id}
            className="pw-preview-media"
            stream={stream}
            showName
            featured={featured}
            audio={audio}
            audioAvailable={!!track}
            onToggleAudio={() => setAudioStreamId(audio ? null : stream.id)}
            fullscreen={fullscreen}
            fullscreenBusy={busy}
            onFullscreen={() => void toggleFullscreen()}
            onDetails={onDetails}
            onVisibilityChange={onVisibilityChange}
            onFeature={onFeature}
            featureBusy={featureBusy}
            canFeature={canFeature}
            featureDisabledReason={featureDisabledReason}
            onSetup={onSetup}
            info={info}
          />
        )}
        {error && <Notice tone="error" alert className="pw-preview-error">{error}</Notice>}
        {audio && track && <AudioTrack trackRef={track} />}
      </div>
    </Dialog>
  )
}
